import ModalLayout from "@/components/layout/ModalLayout";
import ModalHeader from "@/components/ui/ModalHeader";
import { useState } from "react";
import ToggleButton from "@/components/ui/ToggleButton";
import SelectBox from "@/components/ui/SelectBox";
import { useCodeList, useCodeListByPriceExtraCode } from "@/hooks/standard/st_code/useCode";
import { InputBox } from "@/components/ui/InputBox";
import { NumberInputBox } from "@/components/ui/NumberInputBox";
import { Button } from "@/components/ui/Button";
import { toast } from "sonner";
import { usePriceUnitAndExtraInsert } from "@/hooks/standard/st_price_code/usePriceCode";

interface AddModalProps {
    arrivalCodeList: {
        codeId: number;
        name: string;
    }[];
    companyCode: number;
    isOpen: boolean;
    onClose: () => void;
    onSave: () => void;
}

export default function AddModal({ arrivalCodeList, companyCode, isOpen, onClose, onSave }: AddModalProps) {
    const [isUnitToggleOn, setIsUnitToggleOn] = useState(true);
    const [isExtraToggleOn, setIsExtraToggleOn] = useState(false);
    const unitAndExtraInsert = usePriceUnitAndExtraInsert();
    const [arrival, setArrival] = useState("");
    const [departure, setDeparture] = useState("");
    const [distance, setDistance] = useState("");
    const [weight, setWeight] = useState("");
    const [price, setPrice] = useState("");
    const [weightRows, setWeightRows] = useState<{ weight: string; price: string }[]>([]);
    const [extraCode, setExtraCode] = useState("");
    const [extraPrice, setExtraPrice] = useState("");
    const [remark, setRemark] = useState("");
    const { data: extraCodeList } = useCodeListByPriceExtraCode();
    const { data: departureList } = useCodeList({
        parentId: Number(arrival) ?? undefined,
    }, {
        enabled: !!arrival
    });

    const createWeight = () => {
        if (weightRows.length >= 9) {
            toast.warning("무게는 최대 10개까지 추가할 수 있습니다.");
            return;
        }
        if (!isUnitToggleOn) return;
        setWeightRows((prev) => [
            ...prev,
            { weight: "", price: "" },
        ]);
    };

    const handleCancel = () => {
        onClose();
        setIsUnitToggleOn(true);
        setWeightRows([]);
        setIsExtraToggleOn(false);
        setArrival("");
        setDeparture("");
        setDistance("");
        setWeight("");
        setPrice("");
        setExtraCode("");
        setExtraPrice("");
        setRemark("");
    };

    const handleUnitToggleChange = (value: boolean) => {
        // 단가비 토글을 false로 만들려고 할 때, 추가 비용 토글을 자동으로 true로 설정
        if (!value) {
            setIsExtraToggleOn(true);
        }
        setIsUnitToggleOn(value);
    };

    const handleExtraToggleChange = (value: boolean) => {
        // 추가 비용 토글을 false로 만들려고 할 때, 단가비 토글을 자동으로 true로 설정
        if (!value) {
            setIsUnitToggleOn(true);
        }
        setIsExtraToggleOn(value);
    };

    const handleSave = async () => {
        // 먼저 모든 유효성 검사 수행
        if (isUnitToggleOn) {
            if (!arrival || !departure || !weight || !price) {
                toast.error("출발지, 도착지, 무게, 금액을 모두 입력해주세요.");
                return;
            }

            // 추가 행들 유효성 검사
            for (const row of weightRows) {
                if (!row.weight || !row.price) {
                    toast.error("무게와 금액을 모두 입력해주세요.");
                    return;
                }
            }

            const weights = [
                weight,                        // 기본 무게
                ...weightRows.map(r => r.weight) // 추가 행 무게들
            ].filter(w => w); // 빈 값 제거
        
            const uniqueWeights = new Set(weights);
            if (uniqueWeights.size !== weights.length) {
                toast.error("같은 무게 값이 두 번 이상 입력되었습니다.");
                return;
            }
        }

        if (isExtraToggleOn) {
            if (!extraCode || !extraPrice) {
                toast.error("추가 유형 및 비용을 모두 입력해주세요.");
                return;
            }
        }

        try {
            // 단가비 rows 생성
            const unitCodeRows: Array<{
                stPriceUnitCode: number;
                stDist: string;
                stWeight: number;
                stPrice: number;
            }> = [];

            if (isUnitToggleOn) {
                if (weight && price) {
                    unitCodeRows.push({
                        stPriceUnitCode: Number(departure),
                        stDist: distance && distance.trim() ? distance.trim() : "0",
                        stWeight: Number(weight),
                        stPrice: Number(price),
                    });
                }

                // 추가 행들
                for (const row of weightRows) {
                    if (row.weight && row.price) {
                        unitCodeRows.push({
                            stPriceUnitCode: Number(departure),
                            stDist: distance && distance.trim() ? distance.trim() : "0",
                            stWeight: Number(row.weight),
                            stPrice: Number(row.price),
                        });
                    }
                }
            }

            // 추가비용 payload
            const extraPayload = isExtraToggleOn
                ? {
                      stPriceExtraCode: Number(extraCode),
                      stParentCode: Number(companyCode),
                      stExtraPrice: Number(extraPrice),
                      memo: remark,
                  }
                : null;

            // 단가비 / 추가비를 하나의 트랜잭션으로 저장
            await unitAndExtraInsert.mutateAsync({
                priceUnitCodes: isUnitToggleOn ? unitCodeRows : [],
                extraCode: extraPayload,
            });

            // 성공 시에만 모달 닫기 및 저장 완료 처리
            handleCancel();
            onSave();
        } catch (error) {
            // 에러는 훅의 onError에서 처리
            console.log(error);
            return;
        }
    };

    return (
        <ModalLayout isOpen={isOpen} onClose={handleCancel} title="단가코드 추가">
            <ModalHeader
                title="단가비"
                rightComponent={<ToggleButton label="" checked={isUnitToggleOn} onChange={handleUnitToggleChange} />}
            />
            <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="space-y-4">
                    <SelectBox
                        required
                        disabled={!isUnitToggleOn}
                        label="출발지"
                        value={arrival}
                        onChange={value => setArrival(value as string)}
                        options={arrivalCodeList.map((item: any) => ({
                            value: item.codeId,
                            label: item.name,
                        }))}
                    />
                </div>
                <div className="space-y-4">
                    <SelectBox
                        required
                        disabled={!isUnitToggleOn}
                        label="도착지"
                        value={departure}
                        onChange={value => setDeparture(value as string)}
                        options={departureList?.map((item: any) => ({
                            value: item.codeId,
                            label: item.name,
                        })) ?? []}
                    />
                </div>
            </div>
            <div className="flex flex-col min-h-0">
                <div className="border-b border-[#bbbbbb] mb-4">
                    <div className="flex items-center justify-between">
                        <div className="flex justify-start">
                            <p className="text-base font-semibold mb-[6px] ">거리/무게/금액</p>
                        </div>
                        <Button
                                variant="add"
                                className="mb-[6px]"
                                onClick={createWeight}
                            >
                                추가
                            </Button>
                    </div>
                </div>
                <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="space-y-4">
                        <NumberInputBox
                            disabled={!isUnitToggleOn}
                            label="거리(km)"
                            value={distance}
                            onChange={e => setDistance(e.target.value)}
                            min={0}
                        />
                    </div>
                    <div className="space-y-4">
                        <NumberInputBox
                            disabled={!isUnitToggleOn}
                            label="무게(톤)"
                            value={weight}
                            onChange={e => setWeight(e.target.value)}
                            min={0}
                        />
                    </div>
                    <div className="space-y-4">
                        <NumberInputBox
                            disabled={!isUnitToggleOn}
                            label="금액"
                            value={price}
                            onChange={e => setPrice(e.target.value)}
                            min={0}
                        />
                    </div>
                </div>
                {weightRows.map((row, index) => (
                    <div key={index} className="grid grid-cols-3 gap-3 mb-4">
                        <div className="space-y-4 col-start-2">
                            <NumberInputBox
                                label="무게"
                                disabled={!isUnitToggleOn}
                                value={row.weight}
                                onChange={e =>
                                    setWeightRows(prev =>
                                        prev.map((item, i) =>
                                            i === index ? { ...item, weight: e.target.value } : item
                                        )
                                    )
                                }
                                min={0}
                            />
                        </div>
                        <div className="space-y-4">
                            <NumberInputBox
                                disabled={!isUnitToggleOn}
                                label="금액"
                                value={row.price}
                                onChange={e =>
                                    setWeightRows(prev =>
                                        prev.map((item, i) =>
                                            i === index ? { ...item, price: e.target.value } : item
                                        )
                                    )
                                }
                                min={0}
                            />
                        </div>
                    </div>
                ))}
            </div>
            <ModalHeader
                title="추가 비용"
                rightComponent={<ToggleButton label="" checked={isExtraToggleOn} onChange={handleExtraToggleChange} />}
            />
            <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="space-y-4">
                    <SelectBox
                        disabled={!isExtraToggleOn}
                        label="추가"
                        value={extraCode}
                        onChange={value => setExtraCode(value as string)}
                        options={extraCodeList?.map((item: any) => ({
                            value: item.codeId,
                            label: item.name,
                        })) ?? []}
                        dropdownDirection="up"
                    />
                </div>
                <div className="space-y-4">
                    <NumberInputBox
                        disabled={!isExtraToggleOn}
                        label="비용"
                        value={extraPrice}
                        onChange={e => setExtraPrice(e.target.value)}
                    />
                </div>
            </div>
            <div className="flex flex-col min-h-0">
                <div className="gap-1 mb-4">
                    <div className="space-y-4">
                        <InputBox
                            disabled={!isExtraToggleOn}
                            label="비고"
                            value={remark}
                            onChange={e => setRemark(e.target.value)}
                        />
                    </div>
                </div>
            </div>
            <div className="flex items-center gap-3 mt-6">
                <div className="flex justify-start w-full">
                    <Button variant="white" onClick={handleCancel} className="py-[10px]">
                        취소
                    </Button>
                </div>
                <div className="flex justify-end w-full">
                    <Button variant="primary" onClick={handleSave} className="py-[10px]">
                        저장
                    </Button>
                </div>
            </div>
        </ModalLayout>
    );
}